import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Checkbox,
} from "@/components/ui/checkbox";
import { CalendarClock, MapPin, Plus, Trash2, Users, Pencil } from "lucide-react";
import { AvatarBubble } from "@/components/AvatarUploader";

export default function Appointments() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/appointments");
      setItems(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Laden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/users").then(({ data }) => setEmployees(data)).catch(() => {});
    }
  }, [isAdmin]);

  const { upcoming, past } = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const up = [];
    const ps = [];
    items.forEach((a) => {
      const end = new Date((a.end_date || a.date) + "T23:59:59");
      if (end >= today) up.push(a);
      else ps.push(a);
    });
    up.sort((a, b) => (a.date + (a.start_time || "")).localeCompare(b.date + (b.start_time || "")));
    ps.sort((a, b) => (b.date + (b.start_time || "")).localeCompare(a.date + (a.start_time || "")));
    return { upcoming: up, past: ps };
  }, [items]);

  const remove = async (id) => {
    if (!window.confirm("Termin wirklich löschen?")) return;
    try {
      await api.delete(`/appointments/${id}`);
      toast.success("Gelöscht");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  const openEdit = (a) => {
    setEditing(a);
    setDialogOpen(true);
  };

  const closeDialog = (reload) => {
    setDialogOpen(false);
    setEditing(null);
    if (reload) load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <p className="ph-label">Termine</p>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight mt-1">
            Terminkalender
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            {isAdmin
              ? "Termine für dich und dein Team – erscheinen automatisch im Teamkalender."
              : "Deine anstehenden Termine. Der Chef kann dir zusätzliche Termine eintragen."}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => (!o ? closeDialog(false) : setDialogOpen(true))}>
          <DialogTrigger asChild>
            <Button data-testid="btn-new-appointment" className="bg-kauz-navy hover:bg-kauz-navy-dark h-10">
              <Plus className="h-4 w-4 mr-1.5" /> Neuer Termin
            </Button>
          </DialogTrigger>
          <AppointmentDialog
            editing={editing}
            employees={employees}
            isAdmin={isAdmin}
            currentUserId={user.id}
            onSaved={() => closeDialog(true)}
          />
        </Dialog>
      </div>

      <Section title={`Anstehend (${upcoming.length})`} highlight>
        {loading ? (
          <div className="text-slate-400 text-sm py-6 text-center">Lade…</div>
        ) : upcoming.length === 0 ? (
          <EmptyState text="Keine anstehenden Termine." />
        ) : (
          <div className="space-y-2">
            {upcoming.map((a) => (
              <AppointmentCard key={a.id} a={a} isAdmin={isAdmin} onDelete={() => remove(a.id)} onEdit={() => openEdit(a)} />
            ))}
          </div>
        )}
      </Section>

      {past.length > 0 && (
        <Section title={`Vergangen (${past.length})`}>
          <div className="space-y-2 opacity-70">
            {past.slice(0, 10).map((a) => (
              <AppointmentCard key={a.id} a={a} isAdmin={isAdmin} onDelete={() => remove(a.id)} onEdit={() => openEdit(a)} muted />
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children, highlight }) {
  return (
    <section>
      <h2 className={`font-display text-lg font-semibold mb-3 ${highlight ? "text-kauz-navy" : "text-slate-800"}`}>{title}</h2>
      {children}
    </section>
  );
}

function EmptyState({ text }) {
  return (
    <div className="ph-card p-8 text-center text-slate-400">
      <CalendarClock className="h-8 w-8 mx-auto text-slate-300" />
      <p className="text-sm mt-2">{text}</p>
    </div>
  );
}

function AppointmentCard({ a, isAdmin, onDelete, onEdit, muted }) {
  const startDate = new Date(a.date + "T00:00:00");
  const endDate = new Date((a.end_date || a.date) + "T00:00:00");
  const multiDay = a.end_date && a.end_date !== a.date;
  const fmt = (d) => d.toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });
  const timeStr = a.all_day
    ? "Ganztägig"
    : a.start_time && a.end_time
    ? `${a.start_time} – ${a.end_time}`
    : a.start_time || "";

  return (
    <div className={`ph-card p-4 flex gap-3 md:gap-4 items-start border-l-[4px] border-l-kauz-navy ${muted ? "" : ""}`} data-testid={`appt-card-${a.id}`}>
      <div className="hidden md:flex flex-col items-center justify-center bg-slate-50 rounded-lg h-16 w-16 border border-slate-200 flex-shrink-0">
        <span className="text-[10px] uppercase text-slate-500 font-semibold">
          {startDate.toLocaleDateString("de-DE", { month: "short" })}
        </span>
        <span className="text-2xl font-bold text-kauz-navy leading-none tabular-nums">
          {startDate.getDate()}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="font-display text-base font-semibold text-slate-900 truncate">{a.title}</h3>
        <div className="text-xs text-slate-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
          <span className="inline-flex items-center gap-1">
            <CalendarClock className="h-3.5 w-3.5 text-slate-400" />
            {multiDay ? `${fmt(startDate)} → ${fmt(endDate)}` : fmt(startDate)}
            {timeStr && <span className="text-slate-500">· {timeStr}</span>}
          </span>
          {a.location && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5 text-slate-400" />
              {a.location}
            </span>
          )}
        </div>
        {a.description && (
          <p className="text-sm text-slate-600 mt-1.5 whitespace-pre-line">{a.description}</p>
        )}
        {(a.user_names || []).length > 0 && (
          <div className="mt-2 flex items-center gap-1.5 flex-wrap">
            <Users className="h-3.5 w-3.5 text-slate-400" />
            {(a.user_ids || []).map((uid, i) => {
              const name = (a.user_names || [])[i] || "";
              const avatarUrl = (a.attendee_avatars || {})[uid];
              return (
                <span key={uid} className="text-[11px] bg-slate-100 border border-slate-200 rounded-full pl-0.5 pr-2 py-0.5 text-slate-700 inline-flex items-center gap-1.5">
                  <AvatarBubble user={{ id: uid, name }} avatarUrl={avatarUrl} size="xs" />
                  {name}
                </span>
              );
            })}
          </div>
        )}
        <p className="text-[11px] text-slate-400 mt-2">
          Angelegt von {a.created_by_name || "—"}
        </p>
      </div>
      {isAdmin && (
        <div className="flex flex-col gap-1">
          <Button size="sm" variant="outline" className="h-8 w-8 p-0" onClick={onEdit} data-testid={`appt-edit-${a.id}`}>
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="h-8 w-8 p-0 text-rose-700 border-rose-200 hover:bg-rose-50" onClick={onDelete} data-testid={`appt-delete-${a.id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function AppointmentDialog({ editing, employees, isAdmin, currentUserId, onSaved }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [allDay, setAllDay] = useState(false);
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [location, setLocation] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (editing) {
      setTitle(editing.title || "");
      setDescription(editing.description || "");
      setDate(editing.date || new Date().toISOString().slice(0, 10));
      setEndDate(editing.end_date && editing.end_date !== editing.date ? editing.end_date : "");
      setAllDay(!!editing.all_day);
      setStartTime(editing.start_time || "09:00");
      setEndTime(editing.end_time || "10:00");
      setLocation(editing.location || "");
      setSelectedUsers(editing.user_ids || []);
    } else {
      setTitle("");
      setDescription("");
      setDate(new Date().toISOString().slice(0, 10));
      setEndDate("");
      setAllDay(false);
      setStartTime("09:00");
      setEndTime("10:00");
      setLocation("");
      setSelectedUsers(isAdmin ? [currentUserId] : []);
    }
  }, [editing, isAdmin, currentUserId]);

  const toggleUser = (id) => {
    setSelectedUsers((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Titel eingeben");
      return;
    }
    if (isAdmin && selectedUsers.length === 0) {
      toast.error("Mindestens einen Teilnehmer wählen");
      return;
    }
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim(),
        date,
        end_date: endDate || null,
        all_day: allDay,
        start_time: allDay ? null : startTime || null,
        end_time: allDay ? null : endTime || null,
        location: location.trim(),
        user_ids: isAdmin ? selectedUsers : [currentUserId],
      };
      if (editing) {
        await api.patch(`/appointments/${editing.id}`, body);
        toast.success("Termin aktualisiert");
      } else {
        await api.post("/appointments", body);
        toast.success("Termin angelegt");
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setBusy(false);
    }
  };

  return (
    <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-xl">
          {editing ? "Termin bearbeiten" : "Neuer Termin"}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1.5">
          <Label className="ph-label">Titel</Label>
          <Input
            data-testid="appt-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z. B. Fahrzeug-TÜV"
            maxLength={200}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="ph-label">Datum</Label>
            <Input
              type="date"
              data-testid="appt-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label className="ph-label">Bis (optional)</Label>
            <Input
              type="date"
              data-testid="appt-end-date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              min={date}
            />
          </div>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Checkbox checked={allDay} onCheckedChange={setAllDay} data-testid="appt-allday" />
          Ganztägig
        </label>
        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="ph-label">Startzeit</Label>
              <Input
                type="time"
                data-testid="appt-start-time"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label className="ph-label">Endzeit</Label>
              <Input
                type="time"
                data-testid="appt-end-time"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="ph-label">Ort (optional)</Label>
          <Input
            data-testid="appt-location"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="z. B. Werkstatt Müller"
            maxLength={200}
          />
        </div>
        <div className="space-y-1.5">
          <Label className="ph-label">Beschreibung (optional)</Label>
          <Textarea
            data-testid="appt-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            maxLength={2000}
            placeholder="Details zum Termin"
          />
        </div>
        {isAdmin && (
          <div className="space-y-1.5">
            <Label className="ph-label">Teilnehmer</Label>
            <div className="border border-slate-200 rounded-md p-2 max-h-40 overflow-y-auto space-y-1">
              {employees.filter((e) => e.is_active !== false).map((emp) => (
                <label key={emp.id} className="flex items-center gap-2 text-sm text-slate-700 hover:bg-slate-50 rounded px-2 py-1 cursor-pointer">
                  <Checkbox
                    checked={selectedUsers.includes(emp.id)}
                    onCheckedChange={() => toggleUser(emp.id)}
                    data-testid={`appt-user-${emp.id}`}
                  />
                  <AvatarBubble user={emp} size="xs" />
                  <span>{emp.name}</span>
                  {emp.id === currentUserId && <span className="text-[10px] text-slate-400">(ich)</span>}
                </label>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="submit"
            data-testid="appt-submit"
            disabled={busy}
            className="bg-kauz-navy hover:bg-kauz-navy-dark"
          >
            {busy ? "Speichere…" : editing ? "Aktualisieren" : "Anlegen"}
          </Button>
        </div>
      </form>
    </DialogContent>
  );
}
